import {
  addressScheme,
  commonAddressScheme,
  emailScheme,
  isCompleteCityOrSettlement,
  passportSeriesNumberScheme,
  requiredCustomSelectScheme,
  validateConditionaly,
  validateDate,
  validateFullName,
} from '@sravni/cosago-react-library/lib/validationSchemes';
import {
  EMPLOYER_TYPE_SOURCES as EMPLOYER_TYPE_VALUES,
  getValidationFields,
} from '@sravni/creditselection-customer-utils/lib/utils/customers';
import { subYears } from 'date-fns';
import type { MixedSchema, ObjectSchema } from 'yup';
import { boolean, number, object, string } from 'yup';

import { AMOUNTS } from 'constants/amounts';
import { CURRENCY_CHAR } from 'constants/currency';
import { GENDERS } from 'constants/dictionaries';

import { parseFromHumanToIsoDate } from 'shared/lib';
import { FieldErrors } from 'shared/lib/fields';
import { formatNumber } from 'shared/lib/formatters';
import { differentFromFields, validateSurname } from 'shared/lib/validations';
import type { TDadataFullNameSuggest } from 'shared/types/TDaData';
import type { TFullData } from 'shared/types/TUserAddressFields';

import { validateAutoModelYear } from 'entities/creditForm/lib/validationFunctions/validateAutoModelYear';

import type { TCreditPurposeFormViewOptions } from '../lib/formProductType/creditPurposeFormView';
import {
  checkWorkAddress,
  isActivePassport,
  isCorrectInn,
  isCorrectLastWorkExperienceMonth,
  isCorrectSnils,
  isCorrectUnitCode,
  isDateLaterThanOtherDateField,
  isPassportDateCorrect,
  isPassportSeriesValid,
  isRussianLettersWithNoFirstNumber,
  isWorkOrganizationNameNotOnlyIp,
  validateAdditionalPhoneOwnerFio,
  validateAnotherUnemploymentReason,
  validateAutoFieldIfAutoPurpose,
  validateMinMaxStringNumber,
  validateSchemaIfTrueValue,
  validateWorkFieldIfDepsEmploymentType,
} from '../lib/validationFunctions';
import type {
  TAdditionalDataFormFields,
  TContactsFormFields,
  TCreditFormCommonFields,
  TCreditPurposeFormFields,
  TLandingFormFields,
  TPassportFormFields,
  TWorkplaceFormFields,
} from '../types';

const validationFields = getValidationFields();

export const commonValidationSchema: ObjectSchema<TCreditFormCommonFields> = object({
  autoBrand: validateAutoFieldIfAutoPurpose<TCreditFormCommonFields['autoBrand']>(requiredCustomSelectScheme()),
  autoModel: validateAutoFieldIfAutoPurpose<TCreditFormCommonFields['autoModel']>(requiredCustomSelectScheme()),
  autoYear: validateAutoFieldIfAutoPurpose<TCreditFormCommonFields['autoYear']>(validateAutoModelYear()),
  purpose: requiredCustomSelectScheme(),
  amount: string()
    .required(FieldErrors.requiredError)
    .test(
      validateMinMaxStringNumber(
        AMOUNTS.amountMin,
        AMOUNTS.amountMax,
        `Сумма кредита от ${formatNumber(AMOUNTS.amountMin)}`,
        `Сумма кредита до ${formatNumber(AMOUNTS.amountMax)}`,
      ),
    ),
  period: requiredCustomSelectScheme(),
  region: commonAddressScheme('региона/населенного пункта').test(
    'test region',
    'Адрес должен содержать корректный регион/населенный пункт',
    (option) => isCompleteCityOrSettlement(option?.data?.city_fias_id, option?.data?.settlement_fias_id),
  ) as MixedSchema<TCreditFormCommonFields['region']>,
  regionLocationRoute: string().optional(),
  regionLocation: object().nullable().optional() as unknown as MixedSchema<TCreditFormCommonFields['regionLocation']>,
  initialFee: validateAutoFieldIfAutoPurpose<TCreditFormCommonFields['initialFee']>(
    string()
      .required(FieldErrors.requiredError)
      .test(
        validateMinMaxStringNumber(
          AMOUNTS.initialFeeMin,
          AMOUNTS.initialFeeMax,
          `Сумма от ${formatNumber(AMOUNTS.initialFeeMin)}`,
          `Сумма до ${formatNumber(AMOUNTS.initialFeeMax)}`,
        ),
      ),
  ),
  email: emailScheme().required(FieldErrors.emptyEmail),
  mobilePhone: validationFields.mobilePhone as MixedSchema<TCreditFormCommonFields['mobilePhone']>,
  userAgreement: boolean<boolean>().isTrue(FieldErrors.userAgreement).required(FieldErrors.userAgreement),
  advertisingAgreement: boolean<boolean>().required(FieldErrors.advertisingAgreement),
  fullName: validateFullName<Dictionaries.TSelectValue<string, TDadataFullNameSuggest['data']>>({
    requiredNameError: FieldErrors.personName,
  }).test(validateSurname),
  gender: string().oneOf(Object.values(GENDERS), FieldErrors.oneOfError).required(FieldErrors.requiredError),
  birthDate: validateDate({
    notLaterDate: subYears(new Date(), 18),
    messageForNotLater: FieldErrors.birthDateLaterError,
    notEarlierDate: subYears(new Date(), 100),
    messageForNotEarlier: FieldErrors.birthDateEarlierError,
  }).required(FieldErrors.requiredError),
  birthPlace: string()
    .required(FieldErrors.requiredError)
    .min(3, FieldErrors.minSymbols(3))
    .test(isRussianLettersWithNoFirstNumber)
    .max(200, FieldErrors.maxSymbols(200)),
  passportNumber: passportSeriesNumberScheme()
    .required(FieldErrors.requiredError)
    .when(['passportIssueDate'], {
      is: (passportIssueDate: string) => {
        return Boolean(parseFromHumanToIsoDate(passportIssueDate));
      },
      then: (schema) => schema.test(isPassportSeriesValid),
    })
    .when(['passportIssueDate'], {
      is: (passportIssueDate: string) => {
        const passportDate = parseFromHumanToIsoDate(passportIssueDate);
        // МВД сломало реестр паспортов, поэтому сервис работает по справочнику от 21 июня 2023 года.
        // И проверять паспорт через dadata нужно только в случае, если дата его выдачи была раньше
        return passportDate && new Date('2023-06-21') >= passportDate;
      },
      then: (schema) => schema.test(isActivePassport),
    }),
  passportIssuedBy: string()
    .required(FieldErrors.requiredError)
    .min(5, FieldErrors.minSymbols(5))
    .max(200, FieldErrors.maxSymbols(200)),
  passportIssueDate: validateDate({
    notLaterDate: new Date(),
    messageForNotLater: FieldErrors.futureDateError,
  })
    .when(['birthDate'], {
      is: (birthDate: string) => Boolean(parseFromHumanToIsoDate(birthDate)),
      then: (schema) => schema.test(isPassportDateCorrect),
    })
    .required(FieldErrors.requiredError),
  unitCode: string().required(FieldErrors.requiredError).test(isCorrectUnitCode),
  registrationAddress: addressScheme<TFullData>() as unknown as MixedSchema<
    TCreditFormCommonFields['registrationAddress']
  >,
  registrationLocationRoute: string().optional(),
  registrationAddressFlat: string().optional(),
  registrationDate: validateDate({
    notLaterDate: new Date(),
    messageForNotLater: FieldErrors.futureDateError,
  })
    .test(isDateLaterThanOtherDateField('birthDate', 'Не ранее даты рождения'))
    .required(FieldErrors.requiredError),
  residenceAddress: addressScheme<TFullData>() as unknown as MixedSchema<TCreditFormCommonFields['residenceAddress']>,
  residenceLocationRoute: string().optional(),
  residenceAddressFlat: string().optional(),
  residenceAddressIsRegistration: boolean<boolean>().required(FieldErrors.requiredError),
  employmentType: requiredCustomSelectScheme(),
  unemploymentReason: validateWorkFieldIfDepsEmploymentType('unemploymentReason', requiredCustomSelectScheme()),
  anotherUnemploymentReason: validateAnotherUnemploymentReason(
    string()
      .required(FieldErrors.requiredError)
      .min(5, FieldErrors.minSymbols(5))
      .max(100, FieldErrors.maxSymbols(100)),
  ),
  monthlySalary: validateWorkFieldIfDepsEmploymentType(
    'monthlySalary',
    string()
      .required(FieldErrors.requiredError)
      .test(
        validateMinMaxStringNumber(
          AMOUNTS.monthlySalaryMin,
          AMOUNTS.monthlySalaryMax,
          `Минимальный доход - ${formatNumber(AMOUNTS.monthlySalaryMin)} ${CURRENCY_CHAR}`,
          `Максимальный доход - ${formatNumber(AMOUNTS.monthlySalaryMax)} ${CURRENCY_CHAR}`,
        ),
      ),
  ),
  areaOfEmployment: validateWorkFieldIfDepsEmploymentType('areaOfEmployment', requiredCustomSelectScheme()),
  okved: string().optional(),
  employerType: validateWorkFieldIfDepsEmploymentType('employerType', requiredCustomSelectScheme()),
  workPhone: validateWorkFieldIfDepsEmploymentType(
    'workPhone',
    (validationFields.workPhone as MixedSchema<TCreditFormCommonFields['workPhone']>).when(['employerType'], {
      is: (employerType: TCreditFormCommonFields['employerType']) => {
        return employerType?.value === EMPLOYER_TYPE_VALUES.IP;
      },
      then: (schema) => schema.test(differentFromFields(['additionalPhone'], FieldErrors.phonesAreEqual)),
      otherwise: (schema) =>
        schema.test(differentFromFields(['mobilePhone', 'additionalPhone'], FieldErrors.phonesAreEqual)),
    }),
  ),
  jobTitle: validateWorkFieldIfDepsEmploymentType('jobTitle', requiredCustomSelectScheme()),
  employerInn: validateWorkFieldIfDepsEmploymentType('employerInn', string().test(isCorrectInn)),
  lastWorkExperienceStartMonth: validateWorkFieldIfDepsEmploymentType(
    'lastWorkExperienceStartMonth',
    string().required(FieldErrors.requiredError).test(isCorrectLastWorkExperienceMonth),
  ),
  workAddress: validateWorkFieldIfDepsEmploymentType(
    'workAddress',
    object()
      .required(FieldErrors.requiredError)
      .test({
        name: 'workAddressTest',
        message: FieldErrors.notValid,
        test: async (value) => {
          const typedValue = value as TCreditFormCommonFields['workAddress'];
          if (!typedValue || typeof typedValue !== 'object' || !('data' in typedValue)) {
            return false;
          }

          if (!validationFields.workAddress.isValidSync(typedValue.data)) {
            return false;
          }

          return checkWorkAddress(String(typedValue.value || typedValue.label || ''));
        },
      }),
  ),
  workLocationRoute: string().optional(),
  isWorkOrganizationManuallyFilled: boolean().optional(),
  workOrganizationName: validateWorkFieldIfDepsEmploymentType(
    'workOrganizationName',
    string()
      .trim()
      .required(FieldErrors.requiredError)
      .min(3, FieldErrors.minSymbols(3))
      .test('notOnlyIp', FieldErrors.notValid, isWorkOrganizationNameNotOnlyIp),
  ),
  education: requiredCustomSelectScheme(),
  maritalStatus: requiredCustomSelectScheme(),
  numberOfDependents: requiredCustomSelectScheme(),
  additionalPhone: (validationFields.additionalPhone as MixedSchema<TCreditFormCommonFields['additionalPhone']>).when(
    ['employerType'],
    {
      is: (employerType: TCreditFormCommonFields['employerType']) => {
        return employerType?.value === EMPLOYER_TYPE_VALUES.IP;
      },
      then: (schema) => schema.test(differentFromFields(['mobilePhone'], FieldErrors.phonesAreEqual)),
      otherwise: (schema) => schema.test(differentFromFields(['mobilePhone', 'workPhone'], FieldErrors.phonesAreEqual)),
    },
  ),
  additionalPhoneOwner: requiredCustomSelectScheme(),
  additionalPhoneOwnerFullName: validateAdditionalPhoneOwnerFio(
    validateFullName<Dictionaries.TSelectValue<string, TDadataFullNameSuggest['data']>>({
      requiredNameError: FieldErrors.personName,
    }).test(validateSurname),
  ),
  carAvailability: validateConditionaly<TCreditFormCommonFields['carAvailability']>(
    (isToggled) => !!isToggled,
    () =>
      requiredCustomSelectScheme<NonNullable<TCreditFormCommonFields['carAvailability']>>().test(
        (v, context) =>
          v?.value !== 'No' || context.createError({ path: context.path, message: FieldErrors.chooseValue }),
      ),
    () => validateSchemaIfTrueValue(requiredCustomSelectScheme()),
  )('carAvailabilityToggleEnabled'),
  carAvailabilityToggleEnabled: boolean().optional(),
  propertyTypeAvailability: validateConditionaly<TCreditFormCommonFields['propertyTypeAvailability']>(
    (isToggled) => !!isToggled,
    () =>
      requiredCustomSelectScheme<NonNullable<TCreditFormCommonFields['propertyTypeAvailability']>>().test(
        (v, context) =>
          v?.value !== 'Nothing' || context.createError({ path: context.path, message: FieldErrors.chooseValue }),
      ),
    () => validateSchemaIfTrueValue(requiredCustomSelectScheme()),
  )('propertyTypeAvailabilityToggleEnabled'),
  propertyTypeAvailabilityToggleEnabled: boolean().optional(),
  snils: string().optional().test(isCorrectSnils),
  selectedProductType: string().optional() as MixedSchema<TCreditFormCommonFields['selectedProductType']>,
});

export const CreditPurposeFormValidationSchema = (
  options: Partial<TCreditPurposeFormViewOptions> = {},
): ObjectSchema<TCreditPurposeFormFields> => {
  const fields: Array<keyof TCreditPurposeFormFields> = ['autoBrand', 'autoModel', 'autoYear', 'amount', 'initialFee'];

  if (!options.isWithoutPurposeField) {
    fields.push('purpose');
  }

  if (!options.isWithoutPeriod) {
    fields.push('period');
  }

  return commonValidationSchema.pick(fields);
};

export const ContactsFormValidationSchema = (isAuth?: boolean): ObjectSchema<TContactsFormFields> =>
  commonValidationSchema.pick([...(isAuth ? (['email', 'mobilePhone'] as Array<keyof TContactsFormFields>) : [])]);

export const LandingFormValidationSchema = (isAuth?: boolean): ObjectSchema<TLandingFormFields> =>
  commonValidationSchema.pick([
    'autoBrand',
    'autoModel',
    'autoYear',
    'purpose',
    'amount',
    'period',
    'initialFee',
    'fullName',
    'gender',
    'region',
    'regionLocationRoute',
    ...(isAuth
      ? (['email', 'mobilePhone', 'userAgreement', 'advertisingAgreement'] as Array<keyof TLandingFormFields>)
      : []),
  ]);

export const PassportFormValidationSchema = (): ObjectSchema<TPassportFormFields> =>
  commonValidationSchema.pick([
    'fullName',
    'gender',
    'birthDate',
    'birthPlace',
    'passportNumber',
    'passportIssueDate',
    'passportIssuedBy',
    'unitCode',
    'registrationAddress',
    'registrationLocationRoute',
    'registrationAddressFlat',
    'registrationDate',
    'residenceAddress',
    'residenceLocationRoute',
    'residenceAddressFlat',
    'residenceAddressIsRegistration',
    'region',
    'regionLocationRoute',
    'regionLocation',
  ]);

export const WorkplaceFormValidationSchema = (): ObjectSchema<TWorkplaceFormFields> =>
  commonValidationSchema.pick([
    'employmentType',
    'unemploymentReason',
    'anotherUnemploymentReason',
    'monthlySalary',
    'areaOfEmployment',
    'employerType',
    'workPhone',
    'jobTitle',
    'employerInn',
    'lastWorkExperienceStartMonth',
    'workAddress',
    'workLocationRoute',
    'workOrganizationName',
    'isWorkOrganizationManuallyFilled',
    'okved',
  ]);

export const AdditionalInfoFormValidationSchema = (): ObjectSchema<TAdditionalDataFormFields> =>
  commonValidationSchema.pick([
    'education',
    'maritalStatus',
    'numberOfDependents',
    'additionalPhoneOwner',
    'carAvailability',
    'carAvailabilityToggleEnabled',
    'propertyTypeAvailability',
    'propertyTypeAvailabilityToggleEnabled',
    'additionalPhone',
    'snils',
    'additionalPhoneOwnerFullName',
  ]);

export const MfoFullDealFormValidationSchema = () =>
  object({
    period: number().required(FieldErrors.requiredError),
    amount: number().required(FieldErrors.requiredError),
    snils: string().optional().test(isCorrectSnils),
  });
